import { MutableRefObject, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { AddCircleOutline, DeleteOutline, DragIndicator, ExpandMore, KeyboardArrowRight } from '@mui/icons-material'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { com } from '@eidu/form'
import { useSnackBar } from '../../components/feedback/SnackBarProvider'
import PageHeaderActionLabel, { pageHeaderActionButtonSx } from '../../components/layout/PageHeaderActionLabel'
import useEntityRepository from '../../io/useEntityRepository'
import EqualityHashMap from '../../util/EqualityHashMap'
import createEntityToCreate from '../entity/createEntityToCreate'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import DraftFieldValue, {
  draftFieldValueToFieldValue,
  draftFieldValuesToFieldValues,
  fieldValueToDraftFieldValue,
  fieldValueToString,
} from '../../domain/entity/DraftFieldValue'
import EntityFormInput from '../../components/input/EntityFormInput'
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import ReferenceValue = com.eidu.sharedlib.entity.field.ReferenceValue
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import { useAssetTypeRegistry } from '../assets/useAssetTypeRegistry'
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import { useNavigate } from 'react-router-dom'
import {
  isManualOrderField,
  isOrderField,
  ORDER_FIELD_NAME,
  ORDER_FIELD_SUFFIX,
} from '../../util/entityType/orderField'
import { useAssistantContext } from '../../components/assistant/AssistantContext'
import useShallowStableArray from '../../util/useShallowStableArray'

type HierarchyLevelConfig = {
  levelName: string
  entityTypeId: EntityTypeId
  parentFieldId?: FieldId
  effectiveParentFieldId?: FieldId
}

type HierarchyPageProps = {
  hierarchyId: string
  title: string
  levels: readonly HierarchyLevelConfig[]
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
}

type HierarchyNode = {
  id: string
  label: string
  levelIndex: number
  orderValue?: number | null
  parentId?: string
  entity: EntityWithLabelAndRelated
  children: HierarchyNode[]
}

const collectNodeAndDescendantIds = (node: HierarchyNode): readonly string[] => [
  node.id,
  ...node.children.flatMap((child) => collectNodeAndDescendantIds(child)),
]

type CreateDialogState = {
  type: 'create'
  targetLevelIndex: number
  parentNode?: HierarchyNode
}

type EditDialogState = {
  type: 'edit'
  node: HierarchyNode
}

type DeleteDialogState = {
  type: 'delete'
  node: HierarchyNode
}

type DialogState = CreateDialogState | EditDialogState | DeleteDialogState | null

const parentDroppableId = (id: string) => `parent:${id}`

const DROP_TARGET_HEIGHT = 12
const GAP_ID_PREFIX = 'gap:'
const ROOT_GAP_PARENT_ID = 'root'
const MIN_ORDER_VALUE = 0
const MAX_ORDER_VALUE = 1_000_000_000_000_000
const ACTION_BUTTON_SIZE = 32

const getGapId = (parentId: string | undefined, index: number) =>
  `${GAP_ID_PREFIX}${parentId ?? ROOT_GAP_PARENT_ID}:${index}`

const parseGapId = (id: string) => {
  if (!id.startsWith(GAP_ID_PREFIX)) return null
  const [, payload] = id.split(GAP_ID_PREFIX)
  if (!payload) return null
  const [parentToken, indexToken] = payload.split(':')
  if (!parentToken || indexToken === undefined) return null
  const index = Number.parseInt(indexToken, 10)
  if (!Number.isFinite(index)) return null
  return { parentId: parentToken === ROOT_GAP_PARENT_ID ? undefined : parentToken, index }
}

const INDENT_PER_LEVEL = 3

type HierarchyRowDropTargetProps = {
  id: string
  isDragging: boolean
}

const HierarchyRowDropTarget = ({ id, isDragging }: HierarchyRowDropTargetProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
    disabled: !isDragging,
  })

  return (
    <Box sx={{ position: 'relative', height: 0 }}>
      <Box
        ref={setNodeRef}
        sx={{
          position: 'absolute',
          top: -DROP_TARGET_HEIGHT / 2,
          left: 0,
          right: 0,
          height: `${DROP_TARGET_HEIGHT}px`,
          display: 'flex',
          alignItems: 'center',
          pointerEvents: isDragging ? 'auto' : 'none',
          opacity: isDragging ? 1 : 0,
          transition: 'opacity 0.1s ease',
          zIndex: isOver ? 2 : 1,
        }}
      >
        <Box
          sx={{
            height: 2,
            width: '100%',
            bgcolor: isOver ? 'primary.main' : 'divider',
            borderRadius: 1,
            opacity: isOver ? 1 : 0.6,
          }}
        />
      </Box>
    </Box>
  )
}

type HierarchyDragOverlayRowProps = {
  node: HierarchyNode
  depth: number
  isExpanded: boolean
  hasChildLevel: boolean
  hasChildren: boolean
}

const HierarchyDragOverlayRow = ({
  node,
  depth,
  isExpanded,
  hasChildLevel,
  hasChildren,
}: HierarchyDragOverlayRowProps) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      py: 0.5,
      px: 1,
      width: '100%',
      boxSizing: 'border-box',
      borderRadius: 1,
      cursor: 'grabbing',
      bgcolor: 'transparent',
      boxShadow: 0,
      border: '1px solid',
      borderColor: 'divider',
      minHeight: 36,
      pointerEvents: 'none',
    }}
  >
    <Box
      sx={{
        width: (theme) => theme.spacing(depth * INDENT_PER_LEVEL),
        minWidth: (theme) => theme.spacing(depth * INDENT_PER_LEVEL),
        flexShrink: 0,
      }}
    />

    <IconButton size="small" disabled>
      {hasChildren ? (
        isExpanded ? (
          <ExpandMore fontSize="small" />
        ) : (
          <KeyboardArrowRight fontSize="small" />
        )
      ) : (
        <KeyboardArrowRight fontSize="small" sx={{ visibility: 'hidden' }} />
      )}
    </IconButton>

    <Typography
      variant="body2"
      sx={{ flexGrow: 1, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
    >
      {node.label}
    </Typography>

    <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }}>
      {hasChildLevel ? (
        <IconButton
          size="small"
          color="primary"
          disabled
          sx={{ width: ACTION_BUTTON_SIZE, height: ACTION_BUTTON_SIZE }}
        >
          <AddCircleOutline fontSize="small" />
        </IconButton>
      ) : (
        <Box sx={{ width: ACTION_BUTTON_SIZE, height: ACTION_BUTTON_SIZE }} />
      )}
      <IconButton size="small" color="error" disabled sx={{ width: ACTION_BUTTON_SIZE, height: ACTION_BUTTON_SIZE }}>
        <DeleteOutline fontSize="small" />
      </IconButton>
      <IconButton size="small" disabled sx={{ width: ACTION_BUTTON_SIZE, height: ACTION_BUTTON_SIZE }}>
        <DragIndicator fontSize="small" />
      </IconButton>
    </Stack>
  </Box>
)

const mergeRefs =
  <T,>(...refs: readonly (((instance: T | null) => void) | MutableRefObject<T | null> | null)[]) =>
  (node: T | null) => {
    refs.forEach((ref) => {
      if (!ref) return
      if (typeof ref === 'function') ref(node)
      else ref.current = node
    })
  }

const getFieldById = (entityType: EntityType, fieldId: FieldId) =>
  entityType.fields.asJsReadonlyArrayView().find((field) => field.id.equals(fieldId))

const getPrimaryLabelFieldId = (entityType: EntityType): FieldId | undefined => {
  const argumentFields = entityType.label.primary.argumentFields.asJsReadonlyArrayView()
  return argumentFields.length > 0 ? argumentFields[0] : undefined
}

const parseOrderValue = (value: string | undefined | null): number | null => {
  if (value == null) return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number.parseFloat(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

type HierarchyCreatePrefill = {
  hierarchyCreatePrefill: Array<{
    fieldId: string
    value: string
  }>
}

const HierarchyPage = ({ hierarchyId, title, levels, entityTypes }: HierarchyPageProps) => {
  const navigate = useNavigate()
  const { showSnackBar } = useSnackBar()
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const { assetEntityTypesById } = useAssetTypeRegistry(entityTypes)

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const [dialogState, setDialogState] = useState<DialogState>(null)
  const [fieldValues, setFieldValues] = useState<ReadonlyMap<FieldId, DraftFieldValue>>(new Map())
  const [isMutating, setIsMutating] = useState(false)

  const updateFieldValue = (value: DraftFieldValue) =>
    setFieldValues((prev) => new EqualityHashMap(prev).set(value.fieldId, value))

  const canCreateRootLevel = levels.length > 0 && levels[0].parentFieldId === undefined

  const requiresFullPageEditor = (entityType: EntityType) =>
    entityType.kind === EntityTypeKind.Form ||
    entityType.kind === EntityTypeKind.LearningUnit ||
    entityType.kind === EntityTypeKind.User ||
    assetEntityTypesById.has(entityType.id)

  const repositories = levels.map((level, index) =>
    useEntityRepository(
      {
        typeId: level.entityTypeId,
        types: entityTypes,
        pageSize: 500,
      },
      [levels.length, level.entityTypeId.toString(), index]
    )
  )

  const dataByLevel = useShallowStableArray(repositories.map((repo) => repo.useAll()))
  const loadingByLevel = repositories.map((repo) => repo.useLoading())
  const errorByLevel = repositories.map((repo) => repo.useError())

  const firstError = errorByLevel.find((error) => error)

  const isLoadingData = dataByLevel.some((data) => data === undefined) || loadingByLevel.some((loading) => loading)

  const resolveManualOrderField = (levelIndex: number) => {
    const levelConfig = levels[levelIndex]
    const entityType = entityTypes.get(levelConfig.entityTypeId)
    if (!entityType) {
      return { entityType: undefined, parentField: undefined, orderField: undefined, expectedName: ORDER_FIELD_NAME }
    }
    if (levelConfig.parentFieldId) {
      const parentField = getFieldById(entityType, levelConfig.parentFieldId)
      const expectedName = parentField ? `${parentField.name}${ORDER_FIELD_SUFFIX}` : ORDER_FIELD_NAME
      const orderField = parentField
        ? entityType.fields
            .asJsReadonlyArrayView()
            .find((field) => field.name === expectedName && field.type === FieldType.Number)
        : undefined
      return { entityType, parentField, orderField, expectedName }
    }
    const orderField = entityType.fields.asJsReadonlyArrayView().find((field) => isOrderField(field))
    return { entityType, parentField: undefined, orderField, expectedName: ORDER_FIELD_NAME }
  }

  const { roots, nodeMap } = useMemo(() => {
    if (dataByLevel.some((data) => data === undefined))
      return { roots: [] as HierarchyNode[], nodeMap: new Map<string, HierarchyNode>() }

    const nodes = new Map<string, HierarchyNode>()

    const orphanRootIds = new Set<string>()

    const orderFieldsByLevel = levels.map((levelConfig) => {
      const entityType = entityTypes.get(levelConfig.entityTypeId)
      if (!entityType) return undefined
      if (levelConfig.parentFieldId) {
        const parentField = getFieldById(entityType, levelConfig.parentFieldId)
        if (!parentField) return undefined
        const orderFieldName = `${parentField.name}${ORDER_FIELD_SUFFIX}`
        return entityType.fields
          .asJsReadonlyArrayView()
          .find((field) => field.name === orderFieldName && field.type === FieldType.Number)
      }
      return entityType.fields.asJsReadonlyArrayView().find((field) => isOrderField(field))
    })

    levels.forEach((levelConfig, levelIndex) => {
      const entities = dataByLevel[levelIndex]!
      const orderField = orderFieldsByLevel[levelIndex]
      Array.from(entities.entries()).forEach(([entityId, entityWithLabel]) => {
        const id = entityId.toString()
        const primaryLabel = entityWithLabel.primaryLabelText.trim()
        const secondaryLabel = entityWithLabel.secondaryLabelText?.trim() ?? ''
        const baseLabel = primaryLabel.length > 0 ? primaryLabel : '(No label)'
        const label = secondaryLabel.length > 0 ? `${baseLabel} - ${secondaryLabel}` : baseLabel

        let parentId: string | undefined
        if (levelConfig.effectiveParentFieldId) {
          const parentValue = entityWithLabel.entity.valuesByFieldId
            .asJsReadonlyMapView()
            .get(levelConfig.effectiveParentFieldId)
          if (parentValue instanceof ReferenceValue && parentValue.value) parentId = parentValue.value.toString()
        }

        const orderValue = orderField
          ? parseOrderValue(
              fieldValueToString(
                entityWithLabel.entity.valuesByFieldId.asJsReadonlyMapView().get(orderField.id) ?? null
              )
            )
          : null

        nodes.set(id, {
          id,
          label,
          levelIndex,
          orderValue,
          parentId,
          entity: entityWithLabel,
          children: [],
        })
      })
    })

    nodes.forEach((node) => {
      node.children = []
    })

    const rootNodes: HierarchyNode[] = []

    nodes.forEach((node) => {
      if (node.parentId && nodes.has(node.parentId)) {
        nodes.get(node.parentId)!.children.push(node)
      } else if (node.parentId) {
        orphanRootIds.add(node.id)
      } else {
        rootNodes.push(node)
      }
    })

    const compareOrderValue = (a: HierarchyNode, b: HierarchyNode) => {
      const aValue = a.orderValue
      const bValue = b.orderValue
      if (aValue == null && bValue == null) {
        return a.id.localeCompare(b.id, undefined, { sensitivity: 'base' })
      }
      if (aValue == null) return 1
      if (bValue == null) return -1
      if (aValue !== bValue) return aValue - bValue
      return a.id.localeCompare(b.id, undefined, { sensitivity: 'base' })
    }

    const sortNodes = (items: HierarchyNode[]) => {
      if (items.length === 0) return
      const orderField = orderFieldsByLevel[items[0].levelIndex]
      if (orderField) {
        items.sort(compareOrderValue)
      } else {
        items.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      }
      items.forEach((child) => sortNodes(child.children))
    }

    const allOrphanIds = new Set<string>()
    orphanRootIds.forEach((id) => {
      const orphanNode = nodes.get(id)
      if (orphanNode) collectNodeAndDescendantIds(orphanNode).forEach((orphanId) => allOrphanIds.add(orphanId))
    })

    const filteredRootNodes = rootNodes.filter((node) => !allOrphanIds.has(node.id))

    sortNodes(filteredRootNodes)

    allOrphanIds.forEach((id) => nodes.delete(id))

    return { roots: filteredRootNodes, nodeMap: nodes }
  }, [dataByLevel, entityTypes, levels])

  const { setCurrentView } = useAssistantContext()

  const visibleNodes = useMemo(() => {
    const collected: HierarchyNode[] = []
    const walk = (nodes: HierarchyNode[]) => {
      nodes.forEach((node) => {
        collected.push(node)
        if (expandedIds.has(node.id)) walk(node.children)
      })
    }
    walk(roots)
    return collected
  }, [expandedIds, roots])

  const visibleEntities = useMemo(
    () =>
      visibleNodes.slice(0, 20).map((node) => ({
        id: node.id,
        label: node.label,
        typeId: levels[node.levelIndex]?.entityTypeId.toString(),
        typeName: levels[node.levelIndex]?.levelName,
      })),
    [levels, visibleNodes]
  )

  useEffect(() => {
    setCurrentView({
      kind: 'hierarchy',
      hierarchyId,
      title,
      levels: levels.map((level) => ({ levelName: level.levelName, typeId: level.entityTypeId.toString() })),
      visibleEntities,
    })
    return () => {
      setCurrentView(undefined)
    }
  }, [hierarchyId, levels, setCurrentView, title, visibleEntities])

  const activeDragNode = activeDragId ? nodeMap.get(activeDragId) : undefined
  const activeDragTargetLevel = activeDragNode ? activeDragNode.levelIndex - 1 : undefined
  const activeDragCurrentParentId = activeDragNode?.parentId
  const isDragging = activeDragId !== null

  const closeDialog = () => {
    setDialogState(null)
    setFieldValues(new Map())
  }

  const openCreateDialog = (targetLevelIndex: number, parentNode?: HierarchyNode) => {
    setDialogState({ type: 'create', targetLevelIndex, parentNode })
    const levelConfig = levels[targetLevelIndex]
    const entityType = entityTypes.get(levelConfig.entityTypeId)
    if (!entityType) {
      setFieldValues(new Map())
      return
    }
    const initialValues = new EqualityHashMap<FieldId, DraftFieldValue>()
    if (levelConfig.parentFieldId && parentNode) {
      const parentField = getFieldById(entityType, levelConfig.parentFieldId)
      if (parentField) {
        initialValues.set(parentField.id, {
          fieldId: parentField.id,
          type: parentField.type,
          value: parentNode.id,
        })
      }
    }
    setFieldValues(initialValues)
    if (parentNode) setExpandedIds((prev) => new Set(prev).add(parentNode.id))
  }

  const openEditDialog = (node: HierarchyNode) => {
    const levelConfig = levels[node.levelIndex]
    const entityType = entityTypes.get(levelConfig.entityTypeId)
    if (entityType && requiresFullPageEditor(entityType)) {
      navigate(`/hierarchies/${hierarchyId}/${levelConfig.entityTypeId.toString()}/${node.id}/edit`)
      return
    }
    setDialogState({ type: 'edit', node })
    if (!entityType) {
      setFieldValues(new Map())
      return
    }
    const existingValues = new EqualityHashMap<FieldId, DraftFieldValue>()
    entityType.fields.asJsReadonlyArrayView().forEach((field) => {
      const rawValue = node.entity.entity.valuesByFieldId.asJsReadonlyMapView().get(field.id) ?? null
      if (rawValue !== null) {
        existingValues.set(field.id, fieldValueToDraftFieldValue(field.id, rawValue))
      }
    })
    if (levelConfig.parentFieldId) {
      const parentValue =
        node.entity.entity.valuesByFieldId.asJsReadonlyMapView().get(levelConfig.parentFieldId) ?? null
      if (parentValue === null) {
        const parentField = getFieldById(entityType, levelConfig.parentFieldId)
        if (parentField && node.parentId) {
          existingValues.set(parentField.id, {
            fieldId: parentField.id,
            type: parentField.type,
            value: node.parentId,
          })
        }
      }
    }
    setFieldValues(existingValues)
  }

  const openDeleteDialog = (node: HierarchyNode) => {
    setDialogState({ type: 'delete', node })
  }

  const refreshLevel = async (levelIndex: number) => {
    await repositories[levelIndex].refreshAll()
  }

  const createEntity = async () => {
    if (!dialogState || dialogState.type !== 'create') return

    const { targetLevelIndex, parentNode } = dialogState
    const levelConfig = levels[targetLevelIndex]
    const entityType = entityTypes.get(levelConfig.entityTypeId)
    if (!entityType) {
      showSnackBar('Entity type metadata not available', 'error')
      return
    }

    const draftValues: DraftFieldValue[] = entityType.fields.asJsReadonlyArrayView().flatMap((field) => {
      const isParentField = levelConfig.parentFieldId?.equals(field.id) ?? false
      const existing = fieldValues.get(field.id)
      const value = isParentField ? parentNode?.id ?? existing?.value ?? '' : existing?.value ?? ''
      const type = existing?.type ?? field.type
      if (!value && !field.required) return []
      return [
        {
          fieldId: field.id,
          type,
          value,
        },
      ]
    })

    const labelFieldDefined = draftValues.some((draft) => {
      const labelFieldId = getPrimaryLabelFieldId(entityType)
      return labelFieldId && draft.fieldId.equals(labelFieldId) && draft.value.trim().length > 0
    })

    if (!labelFieldDefined) {
      showSnackBar('Please provide a label before saving', 'error')
      return
    }

    setIsMutating(true)
    try {
      const entityToCreate = createEntityToCreate(levelConfig.entityTypeId, draftValues, [], new Map())
      await repositories[targetLevelIndex].createEntity(entityToCreate)
      await refreshLevel(targetLevelIndex)
      if (parentNode) setExpandedIds((prev) => new Set(prev).add(parentNode.id))
      showSnackBar(`${levelConfig.levelName} created`, 'success')
      closeDialog()
    } catch (error) {
      console.error(error)
      showSnackBar('Failed to create entity', 'error')
    } finally {
      setIsMutating(false)
    }
  }

  const editEntity = async () => {
    if (!dialogState || dialogState.type !== 'edit') return

    const { node } = dialogState
    const levelConfig = levels[node.levelIndex]
    const entityType = entityTypes.get(levelConfig.entityTypeId)
    if (!entityType) {
      showSnackBar('Entity type metadata not available', 'error')
      return
    }
    const labelFieldId = getPrimaryLabelFieldId(entityType)
    if (!labelFieldId) {
      showSnackBar('Label field not found for the selected level', 'error')
      return
    }

    setIsMutating(true)
    try {
      const entityType = entityTypes.get(levelConfig.entityTypeId)
      if (!entityType) {
        showSnackBar('Entity type metadata not available', 'error')
        return
      }

      const labelFieldId = getPrimaryLabelFieldId(entityType)
      if (!labelFieldId) {
        showSnackBar('Label field not found for the selected level', 'error')
        return
      }

      const labelDraft = fieldValues.get(labelFieldId)
      if (!labelDraft || labelDraft.value.trim().length === 0) {
        showSnackBar('Please provide a label before saving', 'error')
        return
      }

      const originalValues = node.entity.entity.valuesByFieldId.asJsReadonlyMapView()
      const draftValues: DraftFieldValue[] = entityType.fields
        .asJsReadonlyArrayView()
        .filter((field) => {
          if (levelConfig.parentFieldId?.equals(field.id)) return false
          const draft = fieldValues.get(field.id) ?? { fieldId: field.id, type: field.type, value: '' }
          const originalValue = originalValues.get(field.id) ?? null
          const originalString = fieldValueToString(originalValue)
          return draft.value !== originalString
        })
        .map((field) => fieldValues.get(field.id) ?? { fieldId: field.id, type: field.type, value: '' })

      if (draftValues.length === 0) {
        showSnackBar('No changes to save', 'info')
        closeDialog()
        return
      }

      const patch = {
        valuesToUpdateByFieldId: draftFieldValuesToFieldValues(draftValues),
        linksToUpdate: new Map(),
        externalValuesToUpdate: new Map(),
      }

      await repositories[node.levelIndex].modifyEntity(entityIdFromString(node.id), patch)
      await refreshLevel(node.levelIndex)
      showSnackBar(`${levelConfig.levelName} updated`, 'success')
      closeDialog()
    } catch (error) {
      console.error(error)
      showSnackBar('Failed to update entity', 'error')
    } finally {
      setIsMutating(false)
    }
  }

  const deleteEntity = async () => {
    if (!dialogState || dialogState.type !== 'delete') return

    const { node } = dialogState
    const levelConfig = levels[node.levelIndex]

    setIsMutating(true)
    try {
      await repositories[node.levelIndex].deleteEntity(entityIdFromString(node.id))
      await refreshLevel(node.levelIndex)
      setExpandedIds((prev) => {
        const next = new Set(prev)
        collectNodeAndDescendantIds(node).forEach((id) => next.delete(id))
        return next
      })
      showSnackBar(`${levelConfig.levelName} deleted`, 'success')
      closeDialog()
    } catch (error) {
      console.error(error)
      const message = error instanceof Error && error.message ? `: ${error.message}` : ''
      showSnackBar(`Failed to delete entity${message}`, 'error')
    } finally {
      setIsMutating(false)
    }
  }

  const applyMoveEntity = async (node: HierarchyNode, newParent: HierarchyNode) => {
    const levelConfig = levels[node.levelIndex]
    if (!levelConfig.parentFieldId) return

    const { entityType, parentField, orderField } = resolveManualOrderField(node.levelIndex)
    if (!entityType || !parentField) return

    const currentParentId = node.parentId
    if (currentParentId === newParent.id) return

    const valuesToUpdateByFieldId = new Map<FieldId, ReturnType<typeof draftFieldValueToFieldValue>>()
    valuesToUpdateByFieldId.set(
      parentField.id,
      draftFieldValueToFieldValue({
        fieldId: parentField.id,
        type: parentField.type,
        value: newParent.id,
      })
    )
    if (orderField) valuesToUpdateByFieldId.set(orderField.id, null)

    const patch = {
      valuesToUpdateByFieldId,
      linksToUpdate: new Map(),
      externalValuesToUpdate: new Map(),
    }

    setIsMutating(true)
    try {
      await repositories[node.levelIndex].modifyEntity(entityIdFromString(node.id), patch)
      await refreshLevel(node.levelIndex)
      setExpandedIds((prev) => new Set(prev).add(newParent.id))
    } catch (error) {
      console.error(error)
      showSnackBar('Failed to move entity', 'error')
    } finally {
      setIsMutating(false)
    }
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id.toString())
  }

  const applyGapDrop = async (node: HierarchyNode, parentId: string | undefined, gapIndex: number) => {
    const levelConfig = levels[node.levelIndex]
    const { entityType, parentField, orderField } = resolveManualOrderField(node.levelIndex)
    if (!entityType) {
      showSnackBar('Entity type metadata not available', 'error')
      return
    }
    if (levelConfig.parentFieldId && !parentField) {
      showSnackBar('Parent field not found for the selected level', 'error')
      return
    }

    const siblings = parentId ? nodeMap.get(parentId)?.children : roots
    if (!siblings) return

    const isSameParent = node.parentId === parentId
    const activeIndex = isSameParent ? siblings.findIndex((child) => child.id === node.id) : -1
    const targetIndex = isSameParent && activeIndex !== -1 && gapIndex > activeIndex ? gapIndex - 1 : gapIndex
    const siblingsWithoutActive = isSameParent ? siblings.filter((child) => child.id !== node.id) : siblings
    const clampedIndex = Math.max(0, Math.min(targetIndex, siblingsWithoutActive.length))
    const beforeNode = clampedIndex > 0 ? siblingsWithoutActive[clampedIndex - 1] : undefined
    const afterNode = clampedIndex < siblingsWithoutActive.length ? siblingsWithoutActive[clampedIndex] : undefined
    const lowerBound = beforeNode?.orderValue ?? MIN_ORDER_VALUE
    const upperBound = afterNode?.orderValue ?? MAX_ORDER_VALUE
    const nextOrderValue = Math.floor((lowerBound + upperBound) / 2)

    if (!orderField && (!parentField || isSameParent)) return

    const valuesToUpdateByFieldId = new Map<FieldId, ReturnType<typeof draftFieldValueToFieldValue>>()
    if (parentField && !isSameParent) {
      valuesToUpdateByFieldId.set(
        parentField.id,
        draftFieldValueToFieldValue({
          fieldId: parentField.id,
          type: parentField.type,
          value: parentId ?? '',
        })
      )
    }
    if (orderField) {
      valuesToUpdateByFieldId.set(
        orderField.id,
        draftFieldValueToFieldValue({
          fieldId: orderField.id,
          type: orderField.type,
          value: `${nextOrderValue}`,
        })
      )
    }

    if (valuesToUpdateByFieldId.size === 0) return

    const patch = {
      valuesToUpdateByFieldId,
      linksToUpdate: new Map(),
      externalValuesToUpdate: new Map(),
    }

    setIsMutating(true)
    try {
      await repositories[node.levelIndex].modifyEntity(entityIdFromString(node.id), patch)
      await refreshLevel(node.levelIndex)
      if (parentId && !isSameParent) setExpandedIds((prev) => new Set(prev).add(parentId))
    } catch (error) {
      console.error(error)
      showSnackBar('Failed to update order', 'error')
    } finally {
      setIsMutating(false)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const activeId = event.active.id.toString()
    setActiveDragId(null)
    if (!event.over) return

    const overId = event.over.id.toString()
    const node = nodeMap.get(activeId)
    if (!node) return

    if (overId.startsWith(GAP_ID_PREFIX)) {
      const parsed = parseGapId(overId)
      if (!parsed) return
      void applyGapDrop(node, parsed.parentId, parsed.index)
      return
    }

    if (!overId.startsWith('parent:')) return

    const parentId = overId.replace('parent:', '')
    const parentNode = nodeMap.get(parentId)

    if (!parentNode) return
    if (parentNode.levelIndex !== node.levelIndex - 1) return

    void applyMoveEntity(node, parentNode)
  }

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const canAddChildForLevel = (levelIndex: number) => levelIndex + 1 < levels.length

  const openAddChild = (parentNode: HierarchyNode) => {
    const targetLevelIndex = parentNode.levelIndex + 1
    if (targetLevelIndex >= levels.length) return

    const levelConfig = levels[targetLevelIndex]
    const targetEntityType = entityTypes.get(levelConfig.entityTypeId)
    const prefillState: HierarchyCreatePrefill | undefined = levelConfig.parentFieldId
      ? {
          hierarchyCreatePrefill: [{ fieldId: levelConfig.parentFieldId.toString(), value: parentNode.id }],
        }
      : undefined

    if (targetEntityType && requiresFullPageEditor(targetEntityType)) {
      navigate(`/entities/${levelConfig.entityTypeId.toString()}/new`, {
        state: prefillState,
      })
      return
    }

    openCreateDialog(targetLevelIndex, parentNode)
  }

  const renderNodes = (nodes: HierarchyNode[], parentId?: string) => {
    if (nodes.length === 0) return null
    const hasManualOrder = !!resolveManualOrderField(nodes[0].levelIndex).orderField
    const showDropTargets =
      hasManualOrder &&
      isDragging &&
      activeDragNode &&
      activeDragTargetLevel !== undefined &&
      parentId !== undefined &&
      parentId === activeDragCurrentParentId &&
      nodeMap.get(parentId)?.levelIndex === activeDragTargetLevel

    return (
      <>
        {nodes.map((node, index) => (
          <Box key={node.id}>
            {showDropTargets && <HierarchyRowDropTarget id={getGapId(parentId, index)} isDragging={isDragging} />}
            <HierarchyNodeRow
              node={node}
              depth={node.levelIndex}
              isExpanded={expandedIds.has(node.id)}
              onToggle={() => toggleExpanded(node.id)}
              onAdd={() => (canAddChildForLevel(node.levelIndex) ? openAddChild(node) : undefined)}
              onOpen={() => openEditDialog(node)}
              onDelete={() => openDeleteDialog(node)}
              hasChildLevel={canAddChildForLevel(node.levelIndex)}
              hasChildren={node.children.length > 0}
              draggableEnabled={node.levelIndex > 0}
              droppableEnabled={
                activeDragNode
                  ? activeDragTargetLevel !== undefined &&
                    node.levelIndex === activeDragTargetLevel &&
                    node.id !== activeDragCurrentParentId
                  : false
              }
              activeDragId={activeDragId}
              dragOverlayEnabled={isDragging}
            />
            <Divider sx={{ my: 0.25 }} />
            <Collapse in={expandedIds.has(node.id)} timeout="auto" unmountOnExit>
              <Stack spacing={0} sx={{ mt: 0.25 }}>
                {renderNodes(node.children, node.id)}
              </Stack>
            </Collapse>
          </Box>
        ))}
        {showDropTargets && <HierarchyRowDropTarget id={getGapId(parentId, nodes.length)} isDragging={isDragging} />}
      </>
    )
  }

  const renderCreateDialog = () => {
    if (!dialogState || dialogState.type !== 'create') return null
    const levelConfig = levels[dialogState.targetLevelIndex]
    const entityType = entityTypes.get(levelConfig.entityTypeId)
    if (!entityType) return null
    const parentLabel = dialogState.parentNode?.label ?? ''

    return (
      <Dialog open onClose={isMutating ? undefined : closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add {levelConfig.levelName}</DialogTitle>
        <DialogContent sx={{ overflow: 'visible' }}>
          <Stack spacing={2}>
            {entityType.fields
              .asJsReadonlyArrayView()
              .filter((field) => !isManualOrderField(field))
              .map((field) => {
                const isParentField = levelConfig.parentFieldId?.equals(field.id) ?? false
                if (isParentField) {
                  return (
                    <TextField
                      key={`field-${field.id.toString()}`}
                      label={field.name}
                      value={parentLabel || fieldValues.get(field.id)?.value || ''}
                      disabled
                      fullWidth
                    />
                  )
                }
                return (
                  <EntityFormInput
                    key={`field-${field.id.toString()}`}
                    field={field}
                    fieldValue={fieldValues.get(field.id)}
                    entityTypes={entityTypes}
                    updateField={updateFieldValue}
                  />
                )
              })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isMutating}>
            Cancel
          </Button>
          <Button onClick={createEntity} disabled={isMutating} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  const renderEditDialog = () => {
    if (!dialogState || dialogState.type !== 'edit') return null
    const levelConfig = levels[dialogState.node.levelIndex]
    const entityType = entityTypes.get(levelConfig.entityTypeId)
    if (!entityType) return null
    const parentNode = dialogState.node.parentId ? nodeMap.get(dialogState.node.parentId) : undefined

    return (
      <Dialog open onClose={isMutating ? undefined : closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edit {levelConfig.levelName}</DialogTitle>
        <DialogContent sx={{ overflow: 'visible' }}>
          <Stack spacing={2}>
            {entityType.fields
              .asJsReadonlyArrayView()
              .filter((field) => !isManualOrderField(field))
              .map((field) => {
                const isParentField = levelConfig.parentFieldId?.equals(field.id) ?? false
                if (isParentField) {
                  return (
                    <TextField
                      key={`field-${field.id.toString()}`}
                      label={field.name}
                      value={parentNode?.label ?? fieldValues.get(field.id)?.value ?? ''}
                      disabled
                      fullWidth
                    />
                  )
                }
                return (
                  <EntityFormInput
                    key={`field-${field.id.toString()}`}
                    field={field}
                    fieldValue={fieldValues.get(field.id)}
                    entityTypes={entityTypes}
                    updateField={updateFieldValue}
                  />
                )
              })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isMutating}>
            Cancel
          </Button>
          <Button onClick={editEntity} disabled={isMutating} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Stack spacing={0} sx={{ height: '100%', overflow: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">{title}</Typography>
        {canCreateRootLevel && (
          <Button
            color="primary"
            startIcon={<AddCircleOutline />}
            onClick={() => openCreateDialog(0, undefined)}
            aria-label={`Add ${levels[0]?.levelName ?? ''}`.trim()}
            sx={pageHeaderActionButtonSx}
          >
            <PageHeaderActionLabel>ADD {levels[0]?.levelName}</PageHeaderActionLabel>
          </Button>
        )}
      </Stack>
      <Divider sx={{ mb: 0 }} />

      {firstError ? <Alert severity="error">{String(firstError)}</Alert> : null}

      <Box sx={{ mt: firstError ? 2 : 0, width: '100%' }}>
        {isLoadingData || isMutating ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          </Stack>
        ) : (
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <Stack spacing={0}>{renderNodes(roots)}</Stack>
            {isDragging && activeDragNode ? (
              <DragOverlay>
                <HierarchyDragOverlayRow
                  node={activeDragNode}
                  depth={activeDragNode.levelIndex}
                  isExpanded={expandedIds.has(activeDragNode.id)}
                  hasChildLevel={canAddChildForLevel(activeDragNode.levelIndex)}
                  hasChildren={activeDragNode.children.length > 0}
                />
              </DragOverlay>
            ) : null}
          </DndContext>
        )}
      </Box>

      {renderCreateDialog()}

      {renderEditDialog()}

      {dialogState?.type === 'delete' && (
        <Dialog open onClose={isMutating ? undefined : closeDialog} fullWidth maxWidth="xs">
          <DialogTitle>Delete {levels[dialogState.node.levelIndex].levelName}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography>Are you sure you want to delete "{dialogState.node.label}"?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} disabled={isMutating}>
              Cancel
            </Button>
            <Button onClick={deleteEntity} disabled={isMutating} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  )
}

type HierarchyNodeRowProps = {
  node: HierarchyNode
  depth: number
  isExpanded: boolean
  onToggle: () => void
  onAdd: (() => void) | undefined
  onOpen: () => void
  onDelete: () => void
  hasChildLevel: boolean
  hasChildren: boolean
  draggableEnabled: boolean
  droppableEnabled: boolean
  activeDragId: string | null
  dragOverlayEnabled: boolean
}

const HierarchyNodeRow = ({
  node,
  depth,
  isExpanded,
  onToggle,
  onAdd,
  onOpen,
  onDelete,
  hasChildLevel,
  hasChildren,
  draggableEnabled,
  droppableEnabled,
  activeDragId,
  dragOverlayEnabled,
}: HierarchyNodeRowProps) => {
  const draggable = useDraggable({ id: node.id, disabled: !draggableEnabled })
  const droppable = useDroppable({ id: parentDroppableId(node.id), disabled: !droppableEnabled })

  const combinedRef = mergeRefs<HTMLDivElement>(draggable.setNodeRef, droppable.setNodeRef)

  const isActiveDragSource = activeDragId === node.id
  const isDropTarget = droppable.isOver && droppableEnabled
  const hideWhileDragging = draggable.isDragging && dragOverlayEnabled

  return (
    <Box
      ref={combinedRef}
      onClick={onOpen}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        py: 0.5,
        px: 1,
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: 1,
        transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
        cursor: draggable.isDragging ? 'grabbing' : 'pointer',
        bgcolor: 'common.white',
        boxShadow: isActiveDragSource ? 3 : 0,
        borderWidth: isDropTarget ? 2 : 0,
        borderStyle: 'solid',
        borderColor: isDropTarget ? 'primary.main' : 'transparent',
        minHeight: 36,
      }}
      style={{
        transform: hideWhileDragging ? undefined : CSS.Translate.toString(draggable.transform),
        opacity: hideWhileDragging ? 0.2 : draggable.isDragging ? 0.75 : 1,
      }}
    >
      <Box
        sx={{
          width: (theme) => theme.spacing(depth * INDENT_PER_LEVEL),
          minWidth: (theme) => theme.spacing(depth * INDENT_PER_LEVEL),
          flexShrink: 0,
        }}
      />

      <IconButton
        size="small"
        disabled={!hasChildren}
        onClick={(event) => {
          event.stopPropagation()
          onToggle()
        }}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {hasChildren ? (
          isExpanded ? (
            <ExpandMore fontSize="small" />
          ) : (
            <KeyboardArrowRight fontSize="small" />
          )
        ) : (
          <KeyboardArrowRight fontSize="small" sx={{ visibility: 'hidden' }} />
        )}
      </IconButton>

      <Typography
        variant="body2"
        sx={{ flexGrow: 1, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {node.label}
      </Typography>

      <Stack
        direction="row"
        spacing={0.5}
        sx={{ ml: 'auto' }}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {hasChildLevel ? (
          <Tooltip title="Add child">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() => onAdd?.()}
                sx={{ width: ACTION_BUTTON_SIZE, height: ACTION_BUTTON_SIZE }}
              >
                <AddCircleOutline fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Box sx={{ width: ACTION_BUTTON_SIZE, height: ACTION_BUTTON_SIZE }} />
        )}
        <IconButton
          size="small"
          color="error"
          onClick={onDelete}
          sx={{ width: ACTION_BUTTON_SIZE, height: ACTION_BUTTON_SIZE }}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
        {draggableEnabled ? (
          <Tooltip title="Drag to move">
            <span>
              <IconButton
                size="small"
                ref={draggable.setActivatorNodeRef}
                sx={{ width: ACTION_BUTTON_SIZE, height: ACTION_BUTTON_SIZE }}
                {...draggable.attributes}
                {...draggable.listeners}
              >
                <DragIndicator fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Box sx={{ width: ACTION_BUTTON_SIZE, height: ACTION_BUTTON_SIZE }} />
        )}
      </Stack>
    </Box>
  )
}

export type { HierarchyLevelConfig }
export default HierarchyPage
